import {
  Dropdown,
  Field,
  makeStyles,
  Option,
  Switch,
  tokens,
} from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import {
  ChoiceEdit,
  ChoiceEditorType,
  ChoiceInvalidValueBehavior,
  ChoiceOrientation,
  ChoiceValueSort,
  Section,
  SectionColumn,
} from '@powerportalspro/react-fluent';
import { useMemo, useState } from 'react';

// Localization namespace — reuses the Blazor demo's
// `ChoiceEditPropertiesDemo` keys so both stacks read from the same JSON.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Editors.ChoiceEdit.ChoiceEditPropertiesDemo';

const EDITOR_TYPE_VALUES = [
  ChoiceEditorType.Dropdown,
  ChoiceEditorType.RadioButtons,
] as const;
const ORIENTATION_VALUES = [
  ChoiceOrientation.Vertical,
  ChoiceOrientation.Horizontal,
] as const;
const SORT_VALUES = [
  ChoiceValueSort.Default,
  ChoiceValueSort.DisplayName,
  ChoiceValueSort.DisplayNameDescending,
  ChoiceValueSort.Value,
  ChoiceValueSort.ValueDescending,
] as const;
const INVALID_BEHAVIOR_VALUES = [
  ChoiceInvalidValueBehavior.Disable,
  ChoiceInvalidValueBehavior.Remove,
] as const;
// Tri-state demo control for `displayNullChoice`: "default" leaves the prop
// undefined (required-aware default kicks in), "show"/"hide" pass an explicit
// boolean override.
const DISPLAY_NULL_CHOICE_VALUES = ['default', 'show', 'hide'] as const;
type DisplayNullChoiceMode = (typeof DISPLAY_NULL_CHOICE_VALUES)[number];

// Mirrors the Blazor demo's seed list — option values 100000002 and
// 100000004 are filtered out so the dropdown / radio variants show the
// "Disable" vs. "Remove" behavior visibly.
const INVALID_CHOICE_VALUES = [100000002, 100000004] as const;

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  inlineField: {
    flexShrink: 0,
    gridTemplateColumns: 'auto auto',
    '& label': {
      whiteSpace: 'nowrap',
    },
  },
  selectInput: {
    minWidth: '125px',
  },
});

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function ChoiceEditPropertiesSample() {
  const styles = useStyles();
  const t = useT();
  const [readOnly, setReadOnly] = useState(false);
  const [required, setRequired] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [displayChoiceColors, setDisplayChoiceColors] = useState(true);
  const [editorType, setEditorType] = useState<ChoiceEditorType>(ChoiceEditorType.Dropdown);
  const [orientation, setOrientation] = useState<ChoiceOrientation>(ChoiceOrientation.Vertical);
  const [wrapRadioButtons, setWrapRadioButtons] = useState(true);
  const [sort, setSort] = useState<ChoiceValueSort>(ChoiceValueSort.Default);
  const [invalidValueBehavior, setInvalidValueBehavior] = useState<ChoiceInvalidValueBehavior>(
    ChoiceInvalidValueBehavior.Disable,
  );
  const [displayNullChoice, setDisplayNullChoice] = useState<DisplayNullChoiceMode>('default');

  const editorTypeOptions = useMemo(
    () =>
      EDITOR_TYPE_VALUES.map((value) => ({
        value,
        label: t(`${STRINGS_BASE}.choiceEditorType.${cap(value)}`),
      })),
    [t],
  );
  const orientationOptions = useMemo(
    () =>
      ORIENTATION_VALUES.map((value) => ({
        value,
        label: t(`${STRINGS_BASE}.orientation.${cap(value)}`),
      })),
    [t],
  );
  const sortOptions = useMemo(
    () =>
      SORT_VALUES.map((value) => ({
        value,
        label: t(`${STRINGS_BASE}.sortOrder.${cap(value)}`),
      })),
    [t],
  );
  const invalidBehaviorOptions = useMemo(
    () =>
      INVALID_BEHAVIOR_VALUES.map((value) => ({
        value,
        label: t(`${STRINGS_BASE}.invalidValueBehavior.${cap(value)}`),
      })),
    [t],
  );
  const displayNullChoiceOptions = useMemo(
    () =>
      DISPLAY_NULL_CHOICE_VALUES.map((value) => ({
        value,
        label: t(`${STRINGS_BASE}.displayNullChoice.${cap(value)}`),
      })),
    [t],
  );

  return (
    <Section>
      <SectionColumn
        header={
          <div className={styles.toolbar}>
            <Switch
              label={t(`${STRINGS_BASE}.readonly`)}
              labelPosition="before"
              checked={readOnly}
              onChange={(_, d) => setReadOnly(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.required`)}
              labelPosition="before"
              checked={required}
              onChange={(_, d) => setRequired(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.disabled`)}
              labelPosition="before"
              checked={disabled}
              onChange={(_, d) => setDisabled(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.display-choice-colors`)}
              labelPosition="before"
              checked={displayChoiceColors}
              onChange={(_, d) => setDisplayChoiceColors(d.checked)}
            />
            <Field
              label={t(`${STRINGS_BASE}.sort-order`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={sortOptions.find((o) => o.value === sort)?.label ?? sort}
                selectedOptions={[sort]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setSort(d.optionValue as ChoiceValueSort);
                }}
              >
                {sortOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field
              label={t(`${STRINGS_BASE}.invalid-value-behavior`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={
                  invalidBehaviorOptions.find((o) => o.value === invalidValueBehavior)?.label ??
                  invalidValueBehavior
                }
                selectedOptions={[invalidValueBehavior]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue)
                    setInvalidValueBehavior(d.optionValue as ChoiceInvalidValueBehavior);
                }}
              >
                {invalidBehaviorOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field
              label={t(`${STRINGS_BASE}.display-null-choice`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={
                  displayNullChoiceOptions.find((o) => o.value === displayNullChoice)?.label ??
                  displayNullChoice
                }
                selectedOptions={[displayNullChoice]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setDisplayNullChoice(d.optionValue as DisplayNullChoiceMode);
                }}
              >
                {displayNullChoiceOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field
              label={t(`${STRINGS_BASE}.choice-editor-type`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={
                  editorTypeOptions.find((o) => o.value === editorType)?.label ?? editorType
                }
                selectedOptions={[editorType]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setEditorType(d.optionValue as ChoiceEditorType);
                }}
              >
                {editorTypeOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {editorType === ChoiceEditorType.RadioButtons && (
              <Field
                label={t(`${STRINGS_BASE}.radio-buttons-orientation`)}
                orientation="horizontal"
                className={styles.inlineField}
              >
                <Dropdown
                  className={styles.selectInput}
                  value={
                    orientationOptions.find((o) => o.value === orientation)?.label ?? orientation
                  }
                  selectedOptions={[orientation]}
                  onOptionSelect={(_, d) => {
                    if (d.optionValue) setOrientation(d.optionValue as ChoiceOrientation);
                  }}
                >
                  {orientationOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}
            {editorType === ChoiceEditorType.RadioButtons && orientation === ChoiceOrientation.Horizontal && (
              <Switch
                label={t(`${STRINGS_BASE}.wrap-radio-buttons`)}
                labelPosition="before"
                checked={wrapRadioButtons}
                onChange={(_, d) => setWrapRadioButtons(d.checked)}
              />
            )}
          </div>
        }
      >
        {/* Radio buttons take the section's width, so a horizontal row has room to wrap or scroll in. */}
        <div style={editorType === ChoiceEditorType.RadioButtons ? undefined : { maxWidth: '320px' }}>
          <ChoiceEdit
            columnName="ppp_favoritecolor"
            required={required}
            readOnly={readOnly}
            disabled={disabled}
            displayChoiceColors={displayChoiceColors}
            editorType={editorType}
            invalidChoiceValues={INVALID_CHOICE_VALUES}
            invalidValueBehavior={invalidValueBehavior}
            radioButtonOrientation={orientation}
            wrapRadioButtons={wrapRadioButtons}
            sort={sort}
            {...(displayNullChoice !== 'default'
              ? { displayNullChoice: displayNullChoice === 'show' }
              : {})}
          />
        </div>
      </SectionColumn>
    </Section>
  );
}
